import { useState, useTransition } from "react";
import { useRevalidator } from "react-router";
import { Plus, Trash2, KeyRound, X, Check } from "lucide-react";
import type { AdminUserView } from "@/lib/team/admin-user-view";
import { ADMIN_TIME_ZONE } from "@/lib/format/admin-time";
import { adminFetch } from "@/api";

interface Props {
  admins: AdminUserView[];
  currentAdminId: string;
  canMutate: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: ADMIN_TIME_ZONE,
  });
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case "owner":
      return "bg-amber-500/10 border-amber-500/20 text-amber-300";
    case "viewer":
      return "bg-zinc-500/10 border-zinc-500/20 text-zinc-300";
    default:
      return "bg-emerald-500/10 border-emerald-500/20 text-emerald-300";
  }
}

export function TeamView({ admins, currentAdminId, canMutate }: Props) {
  const { revalidate } = useRevalidator();
  const [pending, startTransition] = useTransition();
  const [showAdd, setShowAdd] = useState(false);
  const [resetFor, setResetFor] = useState<AdminUserView | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reload the team list from the server.
  const refresh = () =>
    startTransition(() => {
      void revalidate();
    });

  const handleDelete = async (row: AdminUserView) => {
    if (!confirm(`Delete ${row.email}? This cannot be undone.`)) return;
    setError(null);
    const res = await adminFetch(`/api/settings/team/${row.id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setError(data.error ?? "Delete failed.");
      return;
    }
    refresh();
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-[12px]">
          {error}
        </div>
      )}

      {canMutate && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-black text-[13px] font-semibold hover:bg-emerald-400 transition-all"
          >
            <Plus className="w-4 h-4" />
            Add admin
          </button>
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/[0.06] text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Created</th>
              <th className="px-5 py-3">Last login</th>
              {canMutate && <th className="px-5 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {admins.map((row) => {
              const isSelf = row.id === currentAdminId;
              return (
                <tr
                  key={row.id}
                  className="border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02]"
                >
                  <td className="px-5 py-3 text-[13px] text-white font-[family-name:var(--font-jetbrains)]">
                    {row.email}
                    {isSelf && (
                      <span className="ml-2 text-[10px] text-zinc-500">(you)</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-zinc-300">{row.name}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${roleBadgeClass(row.role)}`}
                    >
                      {row.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[12px] text-zinc-400">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-[12px] text-zinc-400">
                    {formatDate(row.lastLoginAt)}
                  </td>
                  {canMutate && (
                    <td className="px-5 py-3 text-right space-x-2">
                      <button
                        onClick={() => setResetFor(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[11px] text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-all"
                      >
                        <KeyRound className="w-3 h-3" />
                        Reset
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        disabled={isSelf}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 border border-red-500/20 text-[11px] text-red-300 hover:bg-red-500/15 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        title={isSelf ? "You cannot delete your own account" : undefined}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddAdminDialog
          onCancel={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
          onError={setError}
        />
      )}

      {resetFor && (
        <ResetPasswordDialog
          target={resetFor}
          onCancel={() => setResetFor(null)}
          onDone={() => {
            setResetFor(null);
            refresh();
          }}
          onError={setError}
        />
      )}

      {pending && (
        <p className="text-[11px] text-zinc-500">Refreshing…</p>
      )}
    </div>
  );
}

function DialogShell({
  title,
  children,
  onCancel,
}: {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-[#0b0b0d] border border-white/[0.08] rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-semibold text-white">{title}</h2>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg text-zinc-500 hover:text-white hover:bg-white/[0.04] flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddAdminDialog({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: () => void;
  onError: (msg: string | null) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"owner" | "admin" | "viewer">("admin");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    onError(null);
    const res = await adminFetch("/api/settings/team", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, name, password, role }),
    });
    setSaving(false);
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      onError(data.error ?? "Create failed.");
      return;
    }
    onCreated();
  };

  return (
    <DialogShell title="Add admin" onCancel={onCancel}>
      <div className="space-y-3">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 font-[family-name:var(--font-jetbrains)]"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password (8+ chars, letter + digit)"
          className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 font-[family-name:var(--font-jetbrains)]"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as typeof role)}
          className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white focus:outline-none focus:border-emerald-500/30"
        >
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>
      <button
        onClick={submit}
        disabled={saving || !email || !name || !password}
        className="w-full py-2.5 rounded-xl bg-emerald-500 text-black font-semibold text-sm hover:bg-emerald-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
      >
        {saving ? "Creating…" : (<><Check className="w-4 h-4" /> Create</>)}
      </button>
    </DialogShell>
  );
}

function ResetPasswordDialog({
  target,
  onCancel,
  onDone,
  onError,
}: {
  target: AdminUserView;
  onCancel: () => void;
  onDone: () => void;
  onError: (msg: string | null) => void;
}) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    onError(null);
    const res = await adminFetch(`/api/settings/team/${target.id}/reset-password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
    });
    setSaving(false);
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      onError(data.error ?? "Reset failed.");
      return;
    }
    onDone();
  };

  return (
    <DialogShell title={`Reset password — ${target.email}`} onCancel={onCancel}>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="New password (8+ chars, letter + digit)"
        className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 font-[family-name:var(--font-jetbrains)]"
      />
      <button
        onClick={submit}
        disabled={saving || !password}
        className="w-full py-2.5 rounded-xl bg-emerald-500 text-black font-semibold text-sm hover:bg-emerald-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
      >
        {saving ? "Saving…" : (<><KeyRound className="w-4 h-4" /> Reset password</>)}
      </button>
    </DialogShell>
  );
}
